import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./AdminShared.module.css";
import local from "./AdminTracking.module.css";
import { fetchTrackSummary } from "../../../storage/trackDB";
import {
  itemsToArray,
  countByType,
  countActive,
  isTruthyFlag,
  formatDateDisplay,
  toTitleCase,
} from "./adminUtils";
import { itemHasDraft, isNeverPublished } from "../../../data/cmsDraft";
import { previewItemKey } from "../../../data/cmsPreview";
import { usePreviewStores } from "../../../data/CmsPreviewContext";
import AdminTrackBrowserSetting from "./AdminTrackBrowserSetting";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const catalog = useSelector((state) => state.catalogData.catalog);
  const content = useSelector((state) => state.contentData.content);
  const [trackWeek, setTrackWeek] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchTrackSummary(user, { days: 7 })
        .then((data) => {
          if (!cancelled) setTrackWeek(data);
        })
        .catch(() => {
          if (!cancelled) setTrackWeek(null);
        });
    };
    load();
    window.addEventListener("ms1-admin-refresh-tracking", load);
    return () => {
      cancelled = true;
      window.removeEventListener("ms1-admin-refresh-tracking", load);
    };
  }, [user]);
  const catalogMetadata = useSelector(
    (state) => state.catalogData.catalogMetadata,
  );
  const { previewItems } = usePreviewStores();
  const previewKeySet = useMemo(
    () => new Set((previewItems || []).map((item) => previewItemKey(item))),
    [previewItems],
  );

  const catalogItems = useMemo(() => itemsToArray(catalog), [catalog]);
  const contentItems = useMemo(() => itemsToArray(content), [content]);

  const catalogByType = useMemo(
    () => countByType(catalogItems),
    [catalogItems],
  );
  const contentByType = useMemo(
    () => countByType(contentItems),
    [contentItems],
  );

  const recent = useMemo(() => {
    const merged = [
      ...catalogItems.map((i) => ({ ...i, _section: "catalog" })),
      ...contentItems.map((i) => ({ ...i, _section: "content" })),
    ];
    return merged
      .sort((a, b) => {
        const ad = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bd = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bd - ad;
      })
      .slice(0, 8);
  }, [catalogItems, contentItems]);

  const maxTypeCount = Math.max(1, ...Object.values(catalogByType), 0);
  const servicesActive =
    catalogMetadata?.availableServices?.filter(Boolean)?.length || 0;
  const servicesInactive =
    catalogMetadata?.inactiveServices?.filter(Boolean)?.length || 0;

  return (
    <div>
      <div className={styles["page-header"]}>
        <div className={styles["page-header-content"]}>
          <h1 className={styles["page-title"]}>Dashboard</h1>
          <p className={styles["page-subtitle"]}>
            {catalogItems.length} catalog · {contentItems.length} content
            {servicesActive ? ` · ${servicesActive} services live` : ""}
          </p>
        </div>
        <div className={styles["page-actions"]}>
          <Link to="/manage/catalog/new" className={styles["btn-primary"]}>
            + Catalog item
          </Link>
          <Link to="/manage/content/new" className={styles["btn-secondary"]}>
            + Content item
          </Link>
        </div>
      </div>

      <AdminTrackBrowserSetting variant="dashboard" />

      <div className={styles["stats-grid"]}>
        <div className={styles["stat-card"]}>
          <p className={styles["stat-label"]}>Catalog</p>
          <p className={styles["stat-value"]}>{catalogItems.length}</p>
          <p className={styles["stat-meta"]}>
            {countActive(catalogItems)} active
          </p>
        </div>
        <div className={styles["stat-card"]}>
          <p className={styles["stat-label"]}>Content</p>
          <p className={styles["stat-value"]}>{contentItems.length}</p>
          <p className={styles["stat-meta"]}>
            {countActive(contentItems)} active
          </p>
        </div>
        <div className={styles["stat-card"]}>
          <p className={styles["stat-label"]}>Services</p>
          <p className={styles["stat-value"]}>{servicesActive}</p>
          <p className={styles["stat-meta"]}>{servicesInactive} inactive</p>
        </div>
        <div className={styles["stat-card"]}>
          <p className={styles["stat-label"]}>Types</p>
          <p className={styles["stat-value"]}>
            {Object.keys(catalogByType).length +
              Object.keys(contentByType).length}
          </p>
          <p className={styles["stat-meta"]}>catalog + content</p>
        </div>
        <Link
          to="/manage/tracking"
          className={`${styles["stat-card"]} ${local["stat-link"]}`}
        >
          <p className={styles["stat-label"]}>Landings (7d)</p>
          <p className={styles["stat-value"]}>
            {trackWeek?.totals?.landings != null
              ? trackWeek.totals.landings
              : "—"}
          </p>
          <p className={styles["stat-meta"]}>Tracking</p>
        </Link>
        <Link
          to="/manage/tracking"
          className={`${styles["stat-card"]} ${local["stat-link"]}`}
        >
          <p className={styles["stat-label"]}>Signups (7d)</p>
          <p className={styles["stat-value"]}>
            {trackWeek?.totals?.signups != null
              ? trackWeek.totals.signups
              : "—"}
          </p>
          <p className={styles["stat-meta"]}>Tracking</p>
        </Link>
      </div>

      <div className={styles.panel}>
        <h2 className={styles["panel-title"]}>Catalog by type</h2>
        {Object.keys(catalogByType).length === 0 ? (
          <div className={styles["empty-state"]}>
            <h3>No catalog items yet</h3>
            <p>Add songs, albums, playlists, or streaming services.</p>
            <Link to="/manage/catalog/new" className={styles["btn-primary"]}>
              Create first item
            </Link>
          </div>
        ) : (
          <div className={styles["type-bars"]}>
            {Object.entries(catalogByType)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <button
                  key={type}
                  type="button"
                  className={styles["type-bar-row"]}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    width: "100%",
                    textAlign: "left",
                  }}
                  onClick={() =>
                    navigate(`/manage/catalog?type=${encodeURIComponent(type)}`)
                  }
                >
                  <span>{toTitleCase(type)}</span>
                  <span className={styles["type-bar-track"]}>
                    <span
                      className={styles["type-bar-fill"]}
                      style={{ width: `${(count / maxTypeCount) * 100}%` }}
                    />
                  </span>
                  <span>{count}</span>
                </button>
              ))}
          </div>
        )}
      </div>

      <div className={styles.panel}>
        <h2 className={styles["panel-title"]}>Content by type</h2>
        {Object.keys(contentByType).length === 0 ? (
          <div className={styles["empty-state"]}>
            <h3>No content items yet</h3>
            <p>Notices, about, social connections, and menu order live here.</p>
          </div>
        ) : (
          <div className={styles["type-bars"]}>
            {Object.entries(contentByType)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => {
                const maxC = Math.max(1, ...Object.values(contentByType));
                return (
                  <button
                    key={type}
                    type="button"
                    className={styles["type-bar-row"]}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      width: "100%",
                      textAlign: "left",
                    }}
                    onClick={() =>
                      navigate(
                        `/manage/content?type=${encodeURIComponent(type)}`,
                      )
                    }
                  >
                    <span>{toTitleCase(type)}</span>
                    <span className={styles["type-bar-track"]}>
                      <span
                        className={styles["type-bar-fill"]}
                        style={{ width: `${(count / maxC) * 100}%` }}
                      />
                    </span>
                    <span>{count}</span>
                  </button>
                );
              })}
          </div>
        )}
      </div>

      <div className={styles.panel}>
        <h2 className={styles["panel-title"]}>Recently updated</h2>
        {recent.length === 0 ? (
          <p className={styles["row-muted"]}>No items to show.</p>
        ) : (
          <ul className={styles["quick-list"]}>
            {recent.map((item) => {
              const inPreview =
                Boolean(item.identifier) &&
                previewKeySet.has(
                  previewItemKey({
                    section: item._section,
                    identifier: item.identifier,
                  }),
                );
              return (
                <li key={`${item._section}-${item._id}`}>
                  <Link
                    to={`/manage/${item._section}/edit/${encodeURIComponent(
                      item._id,
                    )}`}
                  >
                    <span>
                      {item.title || item.slug || item._id}
                      <span className={styles["row-muted"]}>
                        {" "}
                        · {item._section} · {item.type || "—"}
                        {isTruthyFlag(item.active) ? "" : " · inactive"}
                        {itemHasDraft(item) ? " · draft" : ""}
                        {isNeverPublished(item) ? " · never published" : ""}
                      </span>
                      {inPreview ? (
                        <>
                          {" "}
                          <span
                            className={`${styles.badge} ${styles["badge-in-preview"]}`}
                            title="Included in the admin preview set"
                          >
                            In preview
                          </span>
                        </>
                      ) : null}
                    </span>
                    <span className={styles["row-muted"]}>
                      {formatDateDisplay(item.updatedAt || item.createdAt)}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
